import React, { createContext, useContext, useState } from 'react';

const GameStateContext = createContext();

export const useGameState = () => {
  const context = useContext(GameStateContext);
  if (!context) {
    throw new Error('useGameState must be used within GameStateProvider');
  }
  return context;
};

export const GameStateProvider = ({ children }) => {
  const [gameState, setGameState] = useState('connecting');
  const [winner, setWinner] = useState(null);
  const [gameOverCountdown, setGameOverCountdown] = useState(0);
  const [lobbyCountdown, setLobbyCountdown] = useState(0);
  const [gameTimer, setGameTimer] = useState(0);
  const [connectionError, setConnectionError] = useState(null);
  const [suddenDeath, setSuddenDeath] = useState(false);
  const [selectedMap, setSelectedMap] = useState('classic');
  const [availableMaps, setAvailableMaps] = useState([]);

  // Memoized actions to prevent unnecessary re-renders
  const actions = React.useMemo(() => ({
    setGameState,
    setWinner,
    setGameOverCountdown,
    setLobbyCountdown,
    setGameTimer,
    setConnectionError,
    setSuddenDeath,
    setSelectedMap,
    setAvailableMaps,
    
    // Helper methods
    resetGameState: () => {
      setWinner(null);
      setGameOverCountdown(0);
      setLobbyCountdown(0);
      setGameTimer(0);
      setConnectionError(null);
      setSuddenDeath(false);
    },

    isInLobby: () => gameState === 'lobby',
    isPlaying: () => gameState === 'playing',
    isGameOver: () => gameState === 'gameOver',
    isConnecting: () => ['connecting', 'reconnecting'].includes(gameState),
    hasError: () => gameState === 'error'
  }), [gameState]);

  // Memoized state values
  const state = React.useMemo(() => ({
    gameState,
    winner,
    gameOverCountdown,
    lobbyCountdown,
    gameTimer,
    connectionError,
    suddenDeath,
    selectedMap,
    availableMaps
  }), [gameState, winner, gameOverCountdown, lobbyCountdown, gameTimer, connectionError, suddenDeath, selectedMap, availableMaps]);

  const value = React.useMemo(() => ({
    ...state,
    ...actions
  }), [state, actions]);

  return (
    <GameStateContext.Provider value={value}>
      {children}
    </GameStateContext.Provider>
  );
};

export default GameStateContext;